import { downloadListsAsExcel } from '../../../services/excelService';
import { GeneratedList } from '../page';

interface ResultsSectionProps {
  lists: GeneratedList[];
}

const TYPE_META: Record<string, { label: string; labelPlural: string; iconClass: string; colorFrom: string; colorTo: string; badgeClass: string }> = {
  individual: {
    label: 'Individual',
    labelPlural: 'Individuales',
    iconClass: 'ri-user-line',
    colorFrom: 'from-indigo-500',
    colorTo: 'to-indigo-700',
    badgeClass: 'bg-indigo-100 text-indigo-700',
  },
  dupla: {
    label: 'Dupla',
    labelPlural: 'Duplas',
    iconClass: 'ri-group-line',
    colorFrom: 'from-emerald-500',
    colorTo: 'to-teal-600',
    badgeClass: 'bg-emerald-100 text-emerald-700',
  },
  trio: {
    label: 'Trío',
    labelPlural: 'Tríos',
    iconClass: 'ri-team-line',
    colorFrom: 'from-amber-500',
    colorTo: 'to-orange-600',
    badgeClass: 'bg-amber-100 text-amber-700',
  },
};

export default function ResultsSection({ lists }: ResultsSectionProps) {
  const totalGroups = lists.reduce((a, l) => a + l.players.length, 0);

  const exportToExcel = () => {
    downloadListsAsExcel(lists);
  };

  const listsByType: Record<string, GeneratedList[]> = {};
  lists.forEach((l) => {
    if (!listsByType[l.type]) listsByType[l.type] = [];
    listsByType[l.type].push(l);
  });

  return (
    <section className="bg-white rounded-xl border border-gray-100 p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
            <i className="ri-file-list-3-line text-xl text-white"></i>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Resultados</h2>
            <p className="text-sm text-gray-500">{lists.length} listas generadas · {totalGroups} registros en total</p>
          </div>
        </div>
        <button
          type="button"
          onClick={exportToExcel}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-download-line text-base"></i>
          Exportar a Excel
        </button>
      </div>

      <div className="space-y-10">
        {(['individual', 'dupla', 'trio'] as const).map((type) => {
          const typeLists = listsByType[type];
          if (!typeLists || typeLists.length === 0) return null;
          const meta = TYPE_META[type];

          return (
            <div key={type}>
              <div className={`flex items-center gap-3 mb-4 px-4 py-3 bg-gradient-to-r ${meta.colorFrom} ${meta.colorTo} rounded-xl`}>
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                  <i className={`${meta.iconClass} text-white text-lg`}></i>
                </div>
                <h3 className="text-base font-bold text-white">{meta.labelPlural}</h3>
                <span className="ml-auto text-sm text-white/80">{typeLists.length} lista{typeLists.length !== 1 ? 's' : ''}</span>
              </div>

              <div className="space-y-5">
                {typeLists.map((list) => {
                  const clubCount: Record<string, number> = {};
                  list.players.forEach((p) => {
                    clubCount[p.club] = (clubCount[p.club] || 0) + 1;
                  });
                  const hasClubConflict = Object.values(clubCount).some((c) => c > 1);

                  return (
                    <div key={list.listNumber} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className={`bg-gradient-to-r ${meta.colorFrom} ${meta.colorTo} px-6 py-4 flex items-center justify-between`}>
                        <h4 className="text-base font-bold text-white flex items-center gap-2">
                          <i className="ri-file-list-line text-lg"></i>
                          Lista {list.listNumber}
                        </h4>
                        <span className="text-sm font-normal text-white/80 flex items-center gap-3">
                          {list.players.length} {meta.label.toLowerCase()}{list.players.length !== 1 ? 's' : ''}
                          {hasClubConflict && (
                            <span className="inline-flex items-center gap-1 text-amber-200 text-xs">
                              <i className="ri-alert-line"></i>
                              Clubs repetidos
                            </span>
                          )}
                        </span>
                      </div>

                      <div className="p-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {list.players.map((player, groupIndex) => {
                            const isClubDuplicated = (clubCount[player.club] || 0) > 1;
                            return (
                              <div
                                key={groupIndex}
                                className={`p-4 rounded-lg border-2 ${
                                  isClubDuplicated
                                    ? 'border-amber-200 bg-amber-50'
                                    : 'border-gray-200 bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <h5 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                    <span className={`w-6 h-6 flex items-center justify-center text-white text-xs rounded-full bg-gradient-to-br ${meta.colorFrom} ${meta.colorTo}`}>
                                      {groupIndex + 1}
                                    </span>
                                    {meta.label} {groupIndex + 1}
                                  </h5>
                                  <div className="flex items-center gap-2">
                                    {isClubDuplicated && (
                                      <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                                        <i className="ri-alert-line"></i>
                                        Club repetido
                                      </span>
                                    )}
                                    <span className="text-xs text-gray-400">#{player.numero}</span>
                                  </div>
                                </div>

                                <div className="space-y-1.5">
                                  {player.jugador1 && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200">
                                      <span className={`w-5 h-5 flex items-center justify-center text-xs rounded-full font-bold ${meta.badgeClass}`}>1</span>
                                      <span className="text-sm font-semibold text-gray-900 truncate">{player.jugador1}</span>
                                    </div>
                                  )}
                                  {(type === 'dupla' || type === 'trio') && player.jugador2 && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200">
                                      <span className={`w-5 h-5 flex items-center justify-center text-xs rounded-full font-bold ${meta.badgeClass}`}>2</span>
                                      <span className="text-sm text-gray-700 truncate">{player.jugador2}</span>
                                    </div>
                                  )}
                                  {type === 'trio' && player.jugador3 && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200">
                                      <span className={`w-5 h-5 flex items-center justify-center text-xs rounded-full font-bold ${meta.badgeClass}`}>3</span>
                                      <span className="text-sm text-gray-700 truncate">{player.jugador3}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="flex items-center gap-2 mt-3">
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white text-gray-600 text-xs font-medium rounded-full border border-gray-200">
                                    <i className="ri-building-line text-xs"></i>
                                    {player.club}
                                  </span>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white text-gray-600 text-xs font-medium rounded-full border border-gray-200">
                                    <i className="ri-trophy-line text-xs"></i>
                                    {player.categoria}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
